import { useEffect, useMemo, useRef } from 'react';

/**
 * {@link useDebouncedCallback} returns a stable debounced wrapper around `callback`: each invocation
 * delays the call until `delayMs` have elapsed without another invocation. The most recent `callback`
 * is always used, and any pending invocation is cancelled on unmount. The returned function also
 * exposes `cancel()` to drop a pending invocation without firing it.
 *
 * Prefer this over a debounced *value* + an effect when the debounced result runs a side effect
 * (e.g. committing a search term to the URL): the work fires from the event handler, so it can't be
 * re-triggered by an unrelated render — such as the value being cleared elsewhere.
 */
export function useDebouncedCallback<Args extends unknown[]>(callback: (...args: Args) => void, delayMs: number) {
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  });

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  return useMemo(() => {
    const debounced = (...args: Args) => {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => callbackRef.current(...args), delayMs);
    };
    debounced.cancel = () => clearTimeout(timeoutRef.current);
    return debounced;
  }, [delayMs]);
}
